import React from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import _ from 'underscore';
import { include } from 'underscore.string';

function formatName(i18n, name) {
  if (name === 'You') {
    return { name: i18n.getString('AuctionInsights_You') };
  }

  if (include(name, ';')) {
    const domains = _.flatten(name.split(';'));
    const count = _.chain(domains)
      .compact()
      .size()
      .value();
    if (count > 3) {
      return {
        name: i18n.getString('Multiple_Competitors_Template', {
          count,
          competitor1: domains[0],
          competitor2: domains[1],
          competitor3: domains[2],
        }),
        fullName: i18n.getString('Multiple_Competitors_Template_Full', {
          count,
          competitors: domains.join(', '),
        }),
      };
    }
  }
  return { name };
}

function generateZones(data) {
  // change null data to dotted lines
  const zones = _.reduce(data, (memo, num, index) => {
    if (_.isNull(num)) {
      const prev = _.findWhere(memo, { value: index });
      if (prev) {
        // If previous found, extend zone duration
        prev.value = index + 1;
      } else {
        // If no previous value found, create new zone around index
        memo.push({ value: index - 1 });
        memo.push({ value: index + 1, dashStyle: 'dot' });
      }
    }
    return memo;
  }, []);

  return {
    zones,
  };
}

function mapSeries(data, categories = []) {
  return {
    data: _.map(data, (value, i) => ([categories[i], value])),
  };
}

function patchMissingData(items, categories, secondarySegment = null) {
  if (_.isEmpty(categories)) return items;

  // patch missing data with null
  return _.map(items, (item) => {
    const data = _.map(
      categories,
      primarySegment =>
        _.result(_.find(
          item.segmentData,
          segment =>
            segment.primarySegment === primarySegment &&
            (secondarySegment ? segment.secondarySegment === secondarySegment : true)
        ), 'value', null)
    );
    return _.extend(item, {
      data,
    });
  });
}

function withSecondMetric(secondaryMetric, metric) {
  return metric === secondaryMetric ?
    {
      yAxis: 1,
      zIndex: 0,
      metric,
    } :
    {
      yAxis: 0,
      zIndex: 1,
      metric,
    };
}

function getSeriesWithSecondMetric(
  i18n,
  items,
  secondarySegment,
  categories,
  secondaryMetric
) {
  const patchedData = patchMissingData(items, categories, secondarySegment);

  return _.map(patchedData, ({
    name, data, metric, visible,
  }) => _.extend(
    mapSeries(data, categories),
    formatName(i18n, name),
    generateZones(data),
    withSecondMetric(secondaryMetric, metric),
    !_.isUndefined(visible) && { visible }
  ));
}

export const WithSeriesData = (WrappedComponent) => {
  const HoC = props => (
    <WrappedComponent
      series={getSeriesWithSecondMetric(
        props.i18n,
        props.items,
        props.secondarySegment,
        props.categories,
        props.secondaryMetric
      )}
      {...props}
    />
  );

  HoC.displayName = `WithSeriesData(${getDisplayName(WrappedComponent)})`;
  HoC.propTypes = {
    secondaryMetric: PropTypes.string,
    secondarySegment: PropTypes.string,
    categories: PropTypes.arrayOf(PropTypes.string),
    i18n: PropTypes.PropTypes.shape({
      getString: PropTypes.func.isRequired,
    }).isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      data: PropTypes.arrayOf(PropTypes.number),
      metric: PropTypes.string,
      segmentData: PropTypes.arrayOf(PropTypes.shape({
        primarySegment: PropTypes.string,
        secondarySegment: PropTypes.string,
        value: PropTypes.number,
      })),
    })).isRequired,
  };
  HoC.defaultProps = {
    secondarySegment: null,
    categories: [],
    secondaryMetric: null,
  };

  hoistNonReactStatics(HoC, WrappedComponent);

  return HoC;
};
